import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import {
  createAdminListingNote,
  fetchAdminListing,
  runAdminListingAction,
} from "../../../services/automapDealerInventoryApi";
import { fetchListingTrustSummary } from "../../../services/automapTrustSafetyApi";
import {
  fetchAdminListingFairPrice,
  recalculateAdminListingFairPrice,
} from "../../../services/automapFairPriceApi";
import {
  fetchAdminListingVehicleRisk,
  recalculateAdminListingVehicleRisk,
} from "../../../services/automapVehicleRiskApi";
import { trackVehicleRiskEvent } from "../../../constants/automapVehicleRiskConstants";
import { RiskLevelPill } from "../../../components/automap/RiskBadge";
import { FAIR_PRICE_LABELS_ES } from "../../../constants/automapFairPriceConstants";
import { getDealerListingPublicUrl } from "../../../utils/dealerListing/dealerListingPublicUrl";
import { CATEGORY_LABELS } from "../../../constants/automapTrustSafetyConstants";
import FairPriceBadge from "../../../components/automap/FairPriceBadge";
import {
  formatListingStatus,
  qualityBadgeClass,
  TRANSMISSION_OPTIONS,
  FUEL_OPTIONS,
} from "../../../constants/automapDealerInventoryConstants";
import {
  ADMIN_LISTING_ACTIONS,
  DUPLICATE_STATUS_LABELS,
  LISTING_NOTE_TYPE_LABELS,
  LISTING_NOTE_TYPES,
  OUTLIER_REASON_LABELS,
} from "../../../constants/automapAdminListingConstants";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  ConfirmDialog,
  formatCop,
  formatDate,
  trackListingAdminEvent,
  vehicleSummary,
} from "./listingAdminShared";
import DraftListingReviewPanel from "../../../components/admin/listings/DraftListingReviewPanel";

function AdminListingDetailPageContent() {
  const { listingId } = useParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [listing, setListing] = useState(null);
  const [trustSummary, setTrustSummary] = useState(null);
  const [fairPrice, setFairPrice] = useState(null);
  const [fairComparables, setFairComparables] = useState([]);
  const [vehicleRisk, setVehicleRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [rejectReason, setRejectReason] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminListing(listingId);
      setListing(data.listing);
      trackListingAdminEvent("admin_listing_opened", { listingId });
      try {
        const trust = await fetchListingTrustSummary(listingId);
        setTrustSummary(trust);
      } catch {
        setTrustSummary(null);
      }
      try {
        const fair = await fetchAdminListingFairPrice(listingId);
        setFairPrice(fair.snapshot);
        setFairComparables(fair.comparables || []);
      } catch {
        setFairPrice(null);
        setFairComparables([]);
      }
      try {
        const risk = await fetchAdminListingVehicleRisk(listingId);
        setVehicleRisk(risk.snapshot || data.listing?.vehicleRisk || null);
      } catch {
        setVehicleRisk(data.listing?.vehicleRisk || null);
      }
    } catch (err) {
      setError(err.message || "Listing no encontrado.");
      setListing(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [listingId]);

  const runAction = async (action, extra = {}) => {
    try {
      await runAdminListingAction(listingId, action, extra);
      trackListingAdminEvent(`admin_listing_${action}`, { listingId });
      setConfirmAction(null);
      setRejectReason("");
      await load();
    } catch (err) {
      setError(err.message || "Acción fallida.");
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      await createAdminListingNote(listingId, { body: noteBody.trim(), noteType });
      trackListingAdminEvent("admin_listing_note_added", { listingId });
      setNoteBody("");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo guardar la nota.");
    }
  };

  const title = listing ? vehicleSummary(listing) : "Listing";
  const publicListingUrl = useMemo(
    () => (listing ? getDealerListingPublicUrl(listing) : null),
    [listing]
  );

  return (
    <AdminPageLayout className="admin-listings-page">
      <AdminAutomotiveNav active="listings" />
      <AdminPageHeader
        eyebrow="AutoMapa · Listing"
        title={title}
        description={listing?.dealer?.businessName || listing?.dealerId}
        primaryActions={
          listing ? (
            <>
              <Link className="admin-btn" to="/admin/listings">Volver</Link>
              <Link className="admin-btn admin-btn--primary" to={`/admin/listings/${listingId}/edit`}>Editar</Link>
              {listing.status === "active" ? (
                publicListingUrl ? (
                  <a
                    className="admin-btn admin-btn--ghost"
                    href={publicListingUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Ver público
                  </a>
                ) : (
                  <span className="admin-btn admin-btn--ghost" title="Sin URL pública canónica todavía.">
                    Público pendiente
                  </span>
                )
              ) : (
                <span className="admin-btn admin-btn--ghost" title="El borrador no es público.">
                  Borrador (no publicado)
                </span>
              )}
            </>
          ) : null
        }
      />
      <main className="admin-console__main">
        {loading ? <AdminStatePanel variant="empty" message="Obteniendo listing…" /> : null}
        {error ? <AdminStatePanel variant="empty" message={error} onRetry={load} retryLabel="Reintentar" /> : null}

        {listing ? (
          <div className="admin-listing-detail-grid">
            <DraftListingReviewPanel listingId={listingId} canManage={canManage} listing={listing} />

            <section className="admin-dashboard-card">
              <h3>Resumen del vehículo</h3>
              <dl className="admin-detail-dl">
                <dt>Marca / modelo</dt><dd>{vehicleSummary(listing)}</dd>
                <dt>Precio</dt><dd>{formatCop(listing.priceCop)}</dd>
                <dt>Kilometraje</dt><dd>{listing.mileageKm != null ? `${listing.mileageKm.toLocaleString("es-CO")} km` : "—"}</dd>
                <dt>Transmisión</dt><dd>{TRANSMISSION_OPTIONS.find((o) => o.value === listing.transmission)?.label || listing.transmission}</dd>
                <dt>Combustible</dt><dd>{FUEL_OPTIONS.find((o) => o.value === listing.fuel)?.label || listing.fuel}</dd>
                <dt>Carrocería</dt><dd>{listing.bodyType || "—"}</dd>
                <dt>Color</dt><dd>{listing.color || "—"}</dd>
                <dt>Ubicación</dt><dd>{[listing.city, listing.neighborhood, listing.address].filter(Boolean).join(", ") || "—"}</dd>
                <dt>Financiación</dt><dd>{listing.financingAvailable ? "Sí" : "No"}</dd>
                <dt>SOAT</dt><dd>{listing.soatStatus || "—"}</dd>
                <dt>Tecnomecánica</dt><dd>{listing.tecnomecanicaStatus || "—"}</dd>
                <dt>Propietarios</dt><dd>{listing.ownershipCount ?? "—"}</dd>
                <dt>Accidentes</dt><dd>{listing.accidentHistoryNote || "—"}</dd>
                <dt>Stock dealer</dt><dd>{listing.dealerStockNumber || "—"}</dd>
                <dt>URL externa</dt><dd>{listing.externalListingUrl ? <a href={listing.externalListingUrl} target="_blank" rel="noreferrer">{listing.externalListingUrl}</a> : "—"}</dd>
              </dl>
              {listing.description ? <p className="admin-listing-description">{listing.description}</p> : null}
            </section>

            <section className="admin-dashboard-card">
              <h3>Estado del listing</h3>
              <dl className="admin-detail-dl">
                <dt>Estado</dt><dd>{formatListingStatus(listing.status)}</dd>
                <dt>Disponibilidad</dt><dd>{formatListingStatus(listing.availabilityStatus)}</dd>
                <dt>Última confirmación</dt><dd>{formatDate(listing.lastConfirmedAt)}</dd>
                <dt>Publicado</dt><dd>{formatDate(listing.publishedAt)}</dd>
                <dt>Vendido</dt><dd>{formatDate(listing.soldAt)}</dd>
                <dt>Archivado</dt><dd>{formatDate(listing.archivedAt)}</dd>
                <dt>Motivo rechazo</dt><dd>{listing.rejectionReason || "—"}</dd>
                <dt>Días obsoleto</dt><dd>{listing.daysStale ?? "—"}</dd>
              </dl>
              {canManage ? (
                <div className="dealer-inventory-actions" style={{ flexWrap: "wrap", marginTop: "1rem" }}>
                  {ADMIN_LISTING_ACTIONS.map(({ action, label, needsReason }) =>
                    needsReason ? (
                      <button key={action} type="button" onClick={() => setConfirmAction({ action, needsReason: true })}>{label}</button>
                    ) : (
                      <button key={action} type="button" onClick={() => (["archive", "reject"].includes(action) ? setConfirmAction({ action }) : runAction(action))}>
                        {label}
                      </button>
                    )
                  )}
                </div>
              ) : null}
            </section>

            {listing.dealer ? (
              <section className="admin-dashboard-card">
                <h3>Concesionario</h3>
                <dl className="admin-detail-dl">
                  <dt>Nombre</dt><dd>{listing.dealer.businessName}</dd>
                  <dt>Verificación</dt><dd>{listing.dealer.verificationStatus}</dd>
                  <dt>Trial</dt><dd>{listing.dealer.trialStatus}</dd>
                  <dt>WhatsApp</dt><dd>{listing.dealer.mainWhatsapp || "—"}</dd>
                  <dt>Listings activos</dt><dd>{listing.dealer.activeListingCount}</dd>
                </dl>
                <div className="admin-console__header-actions">
                  <Link to={`/admin/dealers/${listing.dealer.id}`}>Admin dealer</Link>
                  {listing.dealer.slug ? (
                    <a href={`/v/automap/dealers/${listing.dealer.slug}`} target="_blank" rel="noreferrer">Perfil público</a>
                  ) : null}
                  <Link to={`/admin/listings?dealerId=${listing.dealer.id}`}>Inventario dealer</Link>
                </div>
              </section>
            ) : null}

            <section className="admin-dashboard-card">
              <h3>Fotos ({listing.photoCount ?? 0})</h3>
              <div className="admin-listing-photos">
                {(listing.photos || []).length ? (
                  listing.photos.map((photo, index) => (
                    <img key={photo.url || index} src={photo.url} alt="" width={120} height={90} style={{ objectFit: "cover", borderRadius: 4 }} />
                  ))
                ) : (
                  <AdminStatePanel variant="empty" message="Sin fotos." />
                )}
              </div>
            </section>

            <section className="admin-dashboard-card">
              <h3>Calidad</h3>
              <p>
                Puntaje:{" "}
                <span className={`admin-badge ${qualityBadgeClass(listing.qualityScore)}`}>{listing.qualityScore ?? "—"}</span>
              </p>
              {listing.missingFields?.length ? (
                <ul>
                  {listing.missingFields.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Sin campos faltantes detectados.</p>
              )}
              {listing.outlierReasons?.length ? (
                <p>
                  Atípico:{" "}
                  {listing.outlierReasons.map((r) => (
                    <span key={r} className="admin-badge admin-badge--warning">{OUTLIER_REASON_LABELS[r] || r}</span>
                  ))}
                </p>
              ) : null}
              {listing.duplicateMatches?.length ? (
                <div>
                  <strong>Duplicados posibles:</strong>
                  <ul>
                    {listing.duplicateMatches.slice(0, 5).map((m) => (
                      <li key={m.listingId}>
                        <Link to={`/admin/listings/${m.listingId}`}>{DUPLICATE_STATUS_LABELS[m.type] || m.type}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>

            <section className="admin-dashboard-card">
              <h3>Precio justo</h3>
              {fairPrice ? (
                <>
                  <p>
                    <FairPriceBadge
                      fairPriceLabel={fairPrice.fairPriceLabel}
                      fairPriceScore={fairPrice.fairPriceScore}
                      confidence={fairPrice.confidence}
                      priceDeltaPercent={fairPrice.priceDeltaPercent}
                      comparableCount={fairPrice.comparableCount}
                      variant="admin"
                      hideIfNoData={false}
                      showTooltip={false}
                    />
                  </p>
                  <p>Etiqueta: {FAIR_PRICE_LABELS_ES[fairPrice.fairPriceLabel] || fairPrice.fairPriceLabel}</p>
                  <p>Mediana: {formatCop(fairPrice.medianComparablePriceCop)}</p>
                  <p>Comparables: {fairPrice.comparableCount ?? 0}</p>
                  <p>Delta: {fairPrice.priceDeltaPercent != null ? `${fairPrice.priceDeltaPercent}%` : "—"}</p>
                  <p>Confianza: {fairPrice.confidence}</p>
                  <p>Método: {fairPrice.calculationMethod}</p>
                  <p>Calculado: {formatDate(fairPrice.calculatedAt)}</p>
                  {fairComparables.length ? (
                    <>
                      <h4>Comparables</h4>
                      <ul>
                        {fairComparables.map((c) => (
                          <li key={c.id}>
                            <Link to={`/admin/listings/${c.id}`}>{c.title}</Link>
                            {" · "}{formatCop(c.priceCop)}{" · "}{c.city}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              ) : (
                <p className="muted">Sin snapshot de precio justo.</p>
              )}
              {canManage ? (
                <button
                  type="button"
                  className="admin-btn"
                  onClick={async () => {
                    const snapshot = await recalculateAdminListingFairPrice(listingId);
                    setFairPrice(snapshot);
                    const fair = await fetchAdminListingFairPrice(listingId);
                    setFairComparables(fair.comparables || []);
                  }}
                >
                  Recalcular precio justo
                </button>
              ) : null}
            </section>

            <section className="admin-dashboard-card admin-risk-panel">
              <h3>Indicadores de riesgo</h3>
              {vehicleRisk ? (
                <>
                  <p className="admin-risk-panel__score">
                    <RiskLevelPill riskLevel={vehicleRisk.riskLevel} /> · {vehicleRisk.riskScore}/100
                  </p>
                  <p className="muted">{vehicleRisk.explanation}</p>
                  <p>Calculado: {formatDate(vehicleRisk.calculatedAt)}</p>
                  {vehicleRisk.buyerVisibleIndicators?.length ? (
                    <div className="admin-risk-panel__section">
                      <h4>Visible al comprador</h4>
                      <ul>
                        {vehicleRisk.buyerVisibleIndicators.map((item) => (
                          <li key={item.type}>{item.message}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {vehicleRisk.adminOnlyIndicators?.length ? (
                    <div className="admin-risk-panel__section">
                      <h4>Solo admin</h4>
                      <ul>
                        {vehicleRisk.adminOnlyIndicators.map((item) => (
                          <li key={item.type}>{item.message}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {vehicleRisk.sourceSignals ? (
                    <div className="admin-risk-panel__section">
                      <h4>Señales fuente</h4>
                      <pre style={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(vehicleRisk.sourceSignals, null, 2)}
                      </pre>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="muted">Sin snapshot de riesgo.</p>
              )}
              {canManage ? (
                <button
                  type="button"
                  className="admin-btn"
                  onClick={async () => {
                    trackVehicleRiskEvent("vehicle_risk_admin_recalculate_clicked", { listingId });
                    const result = await recalculateAdminListingVehicleRisk(listingId);
                    setVehicleRisk(result.snapshot);
                  }}
                >
                  Recalcular riesgo
                </button>
              ) : null}
            </section>

            <section className="admin-dashboard-card">
              <h3>Confianza y seguridad</h3>
              {trustSummary ? (
                <>
                  <p>Reportes activos: {trustSummary.reportCount ?? 0}</p>
                  <p>Casos abiertos: {trustSummary.openCaseCount ?? 0}</p>
                  {trustSummary.latestCategory ? (
                    <p>Última categoría: {CATEGORY_LABELS[trustSummary.latestCategory] || trustSummary.latestCategory}</p>
                  ) : (
                    <p className="muted">Sin reportes de compradores.</p>
                  )}
                  {(trustSummary.cases || []).length ? (
                    <ul>
                      {trustSummary.cases.map((c) => (
                        <li key={c.id}>
                          <Link to={`/admin/trust-safety/cases/${c.id}`}>{c.title || c.id}</Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                <p className="muted">Sin datos de confianza.</p>
              )}
              <div className="admin-console__header-actions" style={{ marginTop: "0.75rem" }}>
                <Link to={`/admin/trust-safety/listings`}>Cola listings sospechosos</Link>
                <Link to={`/admin/trust-safety/cases?listingId=${listingId}`}>Ver casos</Link>
              </div>
            </section>

            {listing.importRecord ? (
              <section className="admin-dashboard-card">
                <h3>Importación</h3>
                <dl className="admin-detail-dl">
                  <dt>ID import</dt><dd>{listing.importRecord.id}</dd>
                  <dt>Estado</dt><dd>{listing.importRecord.status}</dd>
                  <dt>Archivo</dt><dd>{listing.importRecord.filename || "—"}</dd>
                </dl>
                <Link to={`/admin/inventory/imports/${listing.importRecord.id}`}>Ver importación</Link>
              </section>
            ) : null}

            <section className="admin-dashboard-card">
              <h3>Leads ({listing.leadsSummary?.total ?? 0})</h3>
              {(listing.leadsSummary?.recent || []).length ? (
                <ul>
                  {listing.leadsSummary.recent.map((lead) => (
                    <li key={lead.id}>{lead.buyerName || lead.id} · {formatDate(lead.createdAt)}</li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Sin leads registrados.</p>
              )}
            </section>

            <section className="admin-dashboard-card">
              <h3>Notas internas</h3>
              {(listing.notes || []).length ? (
                <ul className="admin-activity-list">
                  {listing.notes.map((note) => (
                    <li key={note.id}>
                      <strong>{LISTING_NOTE_TYPE_LABELS[note.noteType] || note.noteType}</strong>
                      <p>{note.body}</p>
                      <span className="muted">{formatDate(note.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Sin notas.</p>
              )}
              {canManage ? (
                <div style={{ marginTop: "1rem" }}>
                  <select value={noteType} onChange={(e) => setNoteType(e.target.value)}>
                    {LISTING_NOTE_TYPES.map((t) => (
                      <option key={t} value={t}>{LISTING_NOTE_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                  <textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Agregar nota interna…" rows={3} style={{ width: "100%", marginTop: 8 }} />
                  <button type="button" className="admin-btn admin-btn--primary" onClick={addNote}>Guardar nota</button>
                </div>
              ) : null}
            </section>

            <section className="admin-dashboard-card">
              <h3>Actividad reciente</h3>
              {(listing.activity || []).length ? (
                <ul className="admin-activity-list">
                  {[...(listing.activity || [])].reverse().slice(0, 20).map((event) => (
                    <li key={event.id}>
                      <strong>{event.title || event.eventType}</strong>
                      {event.description ? <p>{event.description}</p> : null}
                      <span className="muted">{formatDate(event.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Sin eventos registrados.</p>
              )}
            </section>
          </div>
        ) : null}
      </main>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={confirmAction?.action === "reject" ? "Rechazar listing" : "Confirmar acción"}
        message={
          confirmAction?.needsReason ? (
            <span>
              Indique el motivo de rechazo:
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} style={{ width: "100%", marginTop: 8 }} />
            </span>
          ) : (
            `¿Confirmar acción "${confirmAction?.action}"?`
          )
        }
        confirmLabel="Confirmar"
        onCancel={() => { setConfirmAction(null); setRejectReason(""); }}
        onConfirm={() => {
          if (confirmAction?.needsReason) {
            if (!rejectReason.trim()) return;
            runAction("reject", { reason: rejectReason.trim() });
          } else {
            runAction(confirmAction.action);
          }
        }}
      />
    </AdminPageLayout>
  );
}

export default function AdminListingDetailPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminListingDetailPageContent />
    </RequirePermission>
  );
}
